/**
 * Pure scene-model builder for "The Climb" (specs/rank-ladder, specs/coin-based-ranks).
 * Turns the user's coin-based rank into 10 positioned camps along the mountain trail — Icon at
 * the summit (index 10), Rookie at basecamp (index 1) — flagging the current / next camp, sizing
 * and fading by role, and deriving the current camp's COIN progress toward the next rank and the
 * next camp's unlock coin threshold. No React, no RN runtime — unit-tested directly.
 */

import type { ImageSourcePropType } from 'react-native';
import { RANKS, TOP_RANK_INDEX } from '../../constants/ranks.constants';
import { getRank, getPeakCoinRankProgress } from '../../helpers/rankRegistry.helper';
import {
  ABOVE_OPACITY_FAR,
  ABOVE_OPACITY_NEAR,
  CAMP_SIZE,
  NEXT_BUMP,
  SCENE_SLOTS,
} from './rankLadder.constants';

/** Minimal input the climb needs: the coin-based rank, the XP level (flavour), and peak coins. */
export interface RankLadderInput {
  /** Coin-derived rank 1..10 (from useSelfRank). */
  rankIndex: number;
  /** XP level, shown as "· LV N" flavour on the current camp; not what drives the rank. */
  level: number;
  /** All-time-high coin balance — drives the current camp's progress + next camp's unlock. */
  peakCoins: number;
}

export type RankRole = 'current' | 'next' | 'above' | 'below';

export interface RankSceneNode {
  /** 1-based rank index (1 = Rookie … 10 = Icon). */
  index: number;
  name: string;
  badge: ImageSourcePropType;
  /** Fixed slot position (badge centre) + render size in scene px. */
  top: number;
  x: number;
  size: number;
  role: RankRole;
  isCurrent: boolean;
  isNext: boolean;
  /** Always the top rank (Icon) — drives the summit label/glow regardless of role. */
  isSummit: boolean;
  /** 0..1 render opacity (locked ranks above the camp fade toward the peak). */
  opacity: number;
  /** Present only on the current camp. */
  level: number | null;
  /** 0..1 fill of coin progress toward the next rank (field name kept for View compatibility). */
  xpProgress: number | null;
  /** e.g. "12,000 / 20,000 coins" or "Max rank". */
  xpLabel: string | null;
  /** Present only on the next camp: the peak-coin balance at which it unlocks. */
  unlockCoins: number | null;
}

/** Opacity for a locked rank `d` tiers above the camp (d ≥ 2; d = 1 is the un-faded "next"). */
function aboveOpacity(distance: number, maxDistance: number): number {
  if (maxDistance <= 2) return ABOVE_OPACITY_NEAR;
  const t = (distance - 2) / (maxDistance - 2); // 0 at nearest above, 1 at the peak
  return ABOVE_OPACITY_NEAR - t * (ABOVE_OPACITY_NEAR - ABOVE_OPACITY_FAR);
}

/**
 * Build the 10 camps for the scene. Pass `null` (guest / not loaded / errored) to render
 * the informational climb with no highlighted camp.
 */
export function buildRankLadderScene(input: RankLadderInput | null): RankSceneNode[] {
  // Clamp the coin-based rank into 1..10 the same way the header does.
  const currentIndex = input ? getRank(input.rankIndex).index : null;

  return [...RANKS]
    .sort((a, b) => b.index - a.index)
    .map((rank) => {
      const slot = SCENE_SLOTS[rank.index];
      const isSummit = rank.index === TOP_RANK_INDEX;
      const isCurrent = currentIndex != null && rank.index === currentIndex;
      const isNext =
        currentIndex != null &&
        currentIndex < TOP_RANK_INDEX &&
        rank.index === currentIndex + 1;

      let role: RankRole;
      if (isCurrent) role = 'current';
      else if (isNext) role = 'next';
      else if (currentIndex != null && rank.index < currentIndex) role = 'below';
      else role = 'above';

      const size = isCurrent ? CAMP_SIZE : isNext ? slot.baseSize + NEXT_BUMP : slot.baseSize;

      let opacity = 1;
      if (role === 'above' && currentIndex != null) {
        opacity = aboveOpacity(rank.index - currentIndex, TOP_RANK_INDEX - currentIndex);
      }

      const base = {
        index: rank.index,
        name: rank.name,
        badge: rank.badge,
        top: slot.top,
        x: slot.x,
        size,
        role,
        isCurrent,
        isNext,
        isSummit,
        opacity,
        level: null as number | null,
        xpProgress: null as number | null,
        xpLabel: null as string | null,
        unlockCoins: null as number | null,
      };

      if (isNext && input != null) {
        // The next rank unlocks at its coin entry threshold.
        const { nextEntry } = getPeakCoinRankProgress(input.peakCoins);
        return { ...base, unlockCoins: nextEntry };
      }

      if (!isCurrent || input == null) return base;

      const { progress, nextEntry } = getPeakCoinRankProgress(input.peakCoins);
      const atTopRank = nextEntry == null;
      return {
        ...base,
        level: input.level,
        xpProgress: progress,
        xpLabel: atTopRank
          ? 'Max rank'
          : `${Math.floor(input.peakCoins).toLocaleString()} / ${nextEntry.toLocaleString()} coins`,
      };
    });
}
